import { useEffect, useMemo, useRef, useState } from 'react';
// material-ui
import { useTheme } from '@mui/material/styles';

import {
	Avatar,
	Badge,
	Box,
	Card,
	CardHeader,
	ClickAwayListener,
	FormControl,
	IconButton,
	InputAdornment,
	List,
	ListItemAvatar,
	ListItemButton,
	ListItemText,
	OutlinedInput,
	Paper,
	Popper,
	Stack,
	Tab,
	Tabs,
	Typography,
	useMediaQuery,
} from '@mui/material';
// project import
import Transitions from 'components/@extended/Transitions';
import PropTypes from 'prop-types';
// assets
// import { useGetMessagesMutation, useMarkReadMessageMutation } from "store/features/process/processApi";
import { useDispatch, useSelector } from 'react-redux';
import { SearchOutlined } from '@ant-design/icons';
import { Empty } from 'antd';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import ClockIcon from 'assets/icons/clockIcon';
import { CrossIcon, RoundSelectIcon, RoundWarningIcon, TriangleWarningIcon, BriefcaseIcon } from 'assets/icons/common';

import reminder from 'assets/images/icons/reminder.svg';
// import { setMarkAsReadForReminderNotification } from 'store/features/process/processSlice';
import { useGetReminderListMutation } from 'store/features/profile/profileApi';
// import UserAvatar from "sections/chat/UserAvatar";
import ReminderContent from './ReminderContent';

const EmptyUI = (
	<Empty
		style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
		description={<Box variant="span">No Reminder</Box>}
	></Empty>
);

// sx styles
const cardHeaderSX = {
	backgroundColor: '#FFFFFF',
	color: '#0F172A',
	padding: '16px 20px',
	borderBottom: '1px solid #E2E8F0',
	display: 'flex',
	alignItems: 'center',
	'& .MuiCardHeader-content': {
		my: 0,
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiCardHeader-action': {
		m: 0,
		alignSelf: 'center',
		display: 'flex',
		alignItems: 'center',
	},
};

function a11yProps(index) {
	return {
		id: `profile-tab-${index}`,
		'aria-controls': `profile-tabpanel-${index}`,
	};
}

function TabPanel({ children, value, index, ...other }) {
	return (
		<div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
			{value === index && children}
		</div>
	);
}

TabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.any.isRequired,
	value: PropTypes.any.isRequired,
};

// ==============================|| HEADER CONTENT - Message ||============================== //

const Reminder = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
	const navigate = useNavigate();
	const [searchString, setSearchString] = useState('');
	const [GetReminderList, { data: getReminderList }] = useGetReminderListMutation();
	const unreadReminderNotificationCount = useSelector((state) => state?.process?.unreadReminderNotificationCount);
	const [openedReminderList, setOpenedReminderList] = useState([]);
	const [closedReminderList, setClosedReminderList] = useState([]);
	const [reminderList, setReminderList] = useState([]);
	const [tab, setTab] = useState(0);
	const anchorRef = useRef(null);
	const [open, setOpen] = useState(false);

	const clickHandler = (reminder) => {
		if (reminder?.stageId) {
			handleClose();
			navigate('/process-form', {
				state: { stageId: reminder?.stageId, processCode: reminder?.processCode, processVersion: reminder?.processVersion },
			});
		}
	};

	const handleToggle = () => {
		// if (!open) {
		// 	console.log('hello');
		// 	dispatch(setMarkAsReadForReminderNotification());
		// }
		setOpen((prevOpen) => !prevOpen);
	};

	useEffect(() => {
		GetReminderList({});
	}, [GetReminderList]);

	useEffect(() => {
		if (unreadReminderNotificationCount > 0) {
			GetReminderList({});
		}
	}, [unreadReminderNotificationCount, GetReminderList]);

	useEffect(() => {
		if (getReminderList) {
			setReminderList(getReminderList?.reminders);
			let allList = getReminderList?.reminders ?? [];
			let onGoingList = allList.filter((item) => item?.status?.toLowerCase() === 'open');
			let completedList = allList.filter((item) => item?.status?.toLowerCase() === 'closed');
			setOpenedReminderList(onGoingList);
			setClosedReminderList(completedList);
		}
	}, [getReminderList]);

	const handleChange = (event, newValue) => {
		setTab(newValue);
	};

	const handleClose = (event) => {
		if (!open === false) {
			setTab(0);
		}
		if (anchorRef.current && anchorRef.current.contains(event?.target)) {
			return;
		}
		setOpen(false);
	};

	const search = useMemo(() => {
		return reminderList?.filter((item) => {
			const content = `${item?.description} ${item?.data?.positionName}`;
			return content.toLocaleLowerCase().includes(searchString.toLocaleLowerCase());
		});
	}, [searchString, reminderList]);

	return (
		<Box sx={{ alignSelf: 'center', flexShrink: 0 }}>
			{!matchesXs && (
				<IconButton
					disableRipple
					sx={{
						color: 'brandDark.contrastText',
						'&:hover': { color: 'secondary.lighter' },
						width: 'auto',
						height: 'auto',
						padding: '0 12px',
						display: 'flex',
						flexDirection: 'row',
						gap: '8px',
						alignItems: 'center',
					}}
					aria-label="open reminder"
					ref={anchorRef}
					className={openedReminderList?.length > 0 ? 'ringing' : ''}
					onClick={handleToggle}
				>
					<Badge
						color="error"
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						badgeContent={openedReminderList?.length}
						sx={{ '.MuiBadge-colorError': { bgcolor: '#B00020', color: '#ffffff', outline: '1px solid #ffff' } }}
						max={99}
					>
						<Stack direction="column" alignItems="center">
							{/* <img
								src={reminder}
								className={openedReminderList?.length > 0 ? 'ring' : ''}
								width="20px"
								height="auto"
								alt="reminder"
								loading="lazy"
							/> */}
							<ClockIcon width="20px" height="20px" />
						</Stack>
					</Badge>
					<Typography variant="body1">Reminder</Typography>
				</IconButton>
			)}

			{matchesXs && (
				<IconButton
					disableRipple
					sx={{ color: 'brandDark.contrastText', '&:hover': { color: 'secondary.lighter' } }}
					aria-label="open profile"
					ref={anchorRef}
					aria-controls={open ? 'profile-grow' : undefined}
					aria-haspopup="true"
					onClick={handleToggle}
				>
					<Badge
						color="error"
						anchorOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
						badgeContent={openedReminderList?.length}
						sx={{ '.MuiBadge-colorError': { bgcolor: '#B00020', color: '#ffffff', outline: '1px solid #ffff' } }}
						max={99}
					>
						<ClockIcon width="20px" height="20px" />
					</Badge>
				</IconButton>
			)}

			<Popper
				placement={matchesXs ? 'bottom' : 'bottom-end'}
				open={open}
				border="none"
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
				popperOptions={{
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [matchesXs ? -5 : 0, 9],
							},
						},
					],
				}}
			>
				{({ TransitionProps }) => (
					// <Spin spinning={isLoading}>
					<Transitions type="grow" position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
						<Paper
							sx={{
								boxShadow: '0 18px 45px rgba(15, 23, 42, 0.24)',
								minWidth: 500,
								maxWidth: 520,
								width: 520,
								borderRadius: '12px',
								border: '1px solid #E2E8F0',
								overflow: 'hidden',
								[theme.breakpoints.down('sm')]: {
									minWidth: 'unset',
									maxWidth: 'calc(100vw - 24px)',
									width: 'calc(100vw - 24px)',
									margin: '0 12px',
								},
							}}
						>
							<ClickAwayListener onClickAway={handleClose}>
								<Card
									elevation={0}
									sx={{
										position: 'relative',
										borderRadius: '12px',
									}}
								>
									<CardHeader
										titleTypographyProps={{
											sx: {
												fontSize: '20px',
												fontWeight: 600,
												color: '#101828',
												lineHeight: '28px',
											},
										}}
										action={
											<IconButton
												size="small"
												sx={{
													width: 24,
													height: 24,
													p: 0,
													color: '#99A1AF',
													'&:hover': { color: '#99A1AF', backgroundColor: 'transparent' },
												}}
												onClick={handleClose}
											>
												<CrossIcon width="24px" height="24px" color="#99A1AF" />
											</IconButton>
										}
										title="Reminders"
										sx={cardHeaderSX}
									/>
									<Box sx={{ p: '16px 20px 12px' }}>
										<FormControl sx={{ width: '100%' }}>
											<OutlinedInput
												size="small"
												id="reminder-search"
												sx={{
													backgroundColor: '#F8FAFC',
													borderRadius: '12px',
													minHeight: '42px',
													'& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
												}}
												startAdornment={
													<InputAdornment position="start" sx={{ mr: -0.5 }}>
														<SearchOutlined style={{ color: '#94A3B8' }} />
													</InputAdornment>
												}
												aria-describedby="message-search-text"
												inputProps={{
													'aria-label': 'weight',
												}}
												onChange={(e) => setSearchString(e.target.value)}
												placeholder="Search reminders..."
											/>
										</FormControl>
									</Box>
									<Box sx={{ borderBottom: '1px solid #E2E8F0', px: 1.5 }}>
										<Tabs
											variant="fullWidth"
											value={tab}
											onChange={handleChange}
											aria-label="profile tabs"
											sx={{
												'& .MuiTabs-indicator': { backgroundColor: '#2563EB', height: 3, borderRadius: 99 },
												'& .MuiTab-root': {
													fontSize: '14px',
													fontWeight: 400,
													color: '#6A7282',
													textTransform: 'none',
												},
												'& .Mui-selected': { color: '#155DFC !important', fontWeight: 400 },
											}}
										>
											<Tab
												sx={{
													display: 'flex',
													flexDirection: 'row',
													justifyContent: 'center',
													alignItems: 'center',
													textTransform: 'none',
												}}
												icon={
													<RoundWarningIcon
														width="16px"
														height="16px"
														color={tab === 0 ? '#155DFC' : '#6A7282'}
													/>
												}
												label={
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 1 }}>
														<span>Open</span>
														<Box
															component="span"
															sx={{
																minWidth: 22,
																height: 22,
																px: 0.75,
																borderRadius: 99,
																bgcolor: tab === 0 ? '#DBEAFE' : '#E2E8F0',
																color: tab === 0 ? '#1D4ED8' : '#64748B',
																fontSize: '0.75rem',
																fontWeight: 600,
																display: 'inline-flex',
																alignItems: 'center',
																justifyContent: 'center',
															}}
														>
															{openedReminderList?.length || 0}
														</Box>
													</Box>
												}
												{...a11yProps(0)}
											/>
											<Tab
												sx={{
													display: 'flex',
													flexDirection: 'row',
													justifyContent: 'center',
													alignItems: 'center',
													textTransform: 'none',
												}}
												icon={
													<RoundSelectIcon
														width="16px"
														height="16px"
														color={tab === 1 ? '#155DFC' : '#6A7282'}
													/>
												}
												label={
													<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 1 }}>
														<span>Closed</span>
														<Box
															component="span"
															sx={{
																minWidth: 22,
																height: 22,
																px: 0.75,
																borderRadius: 99,
																bgcolor: tab === 1 ? '#DBEAFE' : '#E2E8F0',
																color: tab === 1 ? '#1D4ED8' : '#64748B',
																fontSize: '0.75rem',
																fontWeight: 600,
																display: 'inline-flex',
																alignItems: 'center',
																justifyContent: 'center',
															}}
														>
															{closedReminderList?.length || 0}
														</Box>
													</Box>
												}
												{...a11yProps(1)}
											/>
										</Tabs>
									</Box>
									<List
										component="nav"
										sx={{
											width: '100%',
											maxWidth: '100%',
											height: { xs: 'calc(100dvh - 260px)', md: 430 },
											maxHeight: { xs: 'calc(100dvh - 260px)', md: 430 },
											bgcolor: '#FFFFFF',
											position: 'relative',
											overflow: 'auto',
											overscrollBehavior: 'contain',
											'&::-webkit-scrollbar': {
												width: '6px',
											},
											'&::-webkit-scrollbar-thumb': {
												backgroundColor: '#aeb0b0',
												borderRadius: '6px',
											},
											p: '10px 20px 12px 20px',
											// [theme.breakpoints.down("md")]: {
											//   maxHeight: 450,
											// },
										}}
									>
										{reminderList?.length > 0 ? (
											<>
												{reminderList?.length > 0 && searchString === '' && (
													<Box>
														<TabPanel value={tab} index={0} dir={theme.direction}>
															{openedReminderList?.length > 0 ? (
																<ReminderContent
																	reminderList={openedReminderList}
																	searchString={searchString}
																	clickHandler={clickHandler}
																/>
															) : (
																EmptyUI
															)}
														</TabPanel>
														<TabPanel value={tab} index={1} dir={theme.direction}>
															{closedReminderList?.length > 0 ? (
																<ReminderContent
																	reminderList={closedReminderList}
																	searchString={searchString}
																	clickHandler={clickHandler}
																/>
															) : (
																EmptyUI
															)}
														</TabPanel>
													</Box>
												)}
												{search?.length > 0 && searchString !== '' && (
													<Box>
														<Box sx={{ px: 0, pt: 0, pb: '12px' }}>
															<Typography sx={{ color: '#1E293B', fontSize: '1.04rem', fontWeight: 500 }}>Search</Typography>
														</Box>
														{search?.map((value, index) => (
															<ListItemButton
																key={index}
																sx={{
																	bgcolor: '#F9FAFB',
																	borderRadius: '14px',
																	px: 1.5,
																	py: 1.25,
																	mb: '12px',
																	'&:hover': { bgcolor: '#F3F4F6' },
																}}
																onClick={() => clickHandler(value)}
															>
																<ListItemAvatar>
																	<Avatar
																		alt={value?.type?.toLowerCase() === 'urgent_case' ? 'urgent' : 'briefcase'}
																		sx={{
																			bgcolor: value?.type?.toLowerCase() === 'urgent_case' ? '#FEE2E2' : '#DBEAFE',
																			p: '8px',
																			width: 40,
																			height: 40,
																			borderRadius: '50%',
																			border: 'none',
																		}}
																	>
																		{value?.type?.toLowerCase() === 'urgent_case' ? (
																			<TriangleWarningIcon width="16px" height="16px" />
																		) : (
																			<BriefcaseIcon width="16px" height="16px" />
																		)}
																	</Avatar>
																</ListItemAvatar>
																<ListItemText
																	primary={
																		<Typography variant="h6">
																			{/* <Typography component="span" variant="subtitle1">
                                          {value?.userFrom?.entityFullName}
                                        </Typography>{" "} */}
																			An{' '}
																			<Typography component="span" variant="subtitle1">
																				urgent
																			</Typography>{' '}
																			case no{' '}
																			<Typography component="span" variant="subtitle1">
																				{value?.lynUniqueId}{' '}
																			</Typography>
																			is pending for your action
																			<Typography component="span" variant="subtitle1"></Typography>
																		</Typography>
																	}
																	secondary={moment(value?.createdDate?.localDateTimeString).fromNow()}
																/>
															</ListItemButton>
														))}
													</Box>
												)}
												{searchString !== '' && search.length === 0 && EmptyUI}
											</>
										) : (
											EmptyUI
										)}
									</List>

									<Box sx={{ textAlign: 'center', height: '10px', bgcolor: '#fff' }}></Box>

									{/* <ListItemButton
										sx={{ textAlign: 'center', py: `${8}px !important`, bgcolor: '#0052CC', '&:hover': { bgcolor: '#1890ff' } }}
									>
										<ListItemText
											sx={{
												margin: '0 0',
											}}
											primary={
												<Typography variant="h6" sx={{ color: 'common.white' }}>
													View All
												</Typography>
											}
										/>
									</ListItemButton> */}
								</Card>
							</ClickAwayListener>
						</Paper>
					</Transitions>
					// </Spin>
				)}
			</Popper>
		</Box>
	);
};

export default Reminder;
